import { useEffect, useState, useCallback } from 'react';
import { Button } from './ui/button';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
} from './ui/dialog';
import {
    Smartphone,
    Download,
    X,
    Share,
    Plus,
    Apple,
    Chrome,
    CheckCircle2,
} from 'lucide-react';

const DISMISS_KEY = 'scalimmo:pwa-dismiss-until';
const DISMISS_DAYS = 14;

// Detect if app is already running as installed PWA
const isStandalone = () => {
    if (typeof window === 'undefined') return false;
    return (
        window.matchMedia('(display-mode: standalone)').matches ||
        window.navigator.standalone === true
    );
};

const isIOS = () => {
    if (typeof window === 'undefined') return false;
    return /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
};

const isAndroid = () => {
    if (typeof window === 'undefined') return false;
    return /Android/i.test(navigator.userAgent);
};

const getDismissedUntil = () => {
    try {
        const v = localStorage.getItem(DISMISS_KEY);
        return v ? parseInt(v, 10) : 0;
    } catch { return 0; }
};

const setDismissedFor = (days) => {
    try {
        localStorage.setItem(DISMISS_KEY, String(Date.now() + days * 86400 * 1000));
    } catch { /* noop */ }
};

export const InstallAppPrompt = () => {
    const [deferredPrompt, setDeferredPrompt] = useState(null);
    const [showBanner, setShowBanner] = useState(false);
    const [showIOSDialog, setShowIOSDialog] = useState(false);

    const dismissed = getDismissedUntil() > Date.now();

    // Listen to Android/Chrome beforeinstallprompt
    useEffect(() => {
        if (isStandalone() || dismissed) return;

        const handler = (e) => {
            e.preventDefault();
            setDeferredPrompt(e);
            setShowBanner(true);
        };
        window.addEventListener('beforeinstallprompt', handler);

        // iOS doesn't fire beforeinstallprompt — show banner after a short delay
        if (isIOS() && !isStandalone()) {
            const t = setTimeout(() => setShowBanner(true), 1000);
            return () => {
                clearTimeout(t);
                window.removeEventListener('beforeinstallprompt', handler);
            };
        }

        return () => window.removeEventListener('beforeinstallprompt', handler);
    }, [dismissed]);

    const handleInstall = useCallback(async () => {
        if (deferredPrompt) {
            // Android/Chrome path
            deferredPrompt.prompt();
            const { outcome } = await deferredPrompt.userChoice;
            if (outcome === 'accepted') {
                setShowBanner(false);
            }
            setDeferredPrompt(null);
        } else if (isIOS()) {
            // iOS path: show step-by-step modal
            setShowIOSDialog(true);
        }
    }, [deferredPrompt]);

    const handleDismiss = () => {
        setDismissedFor(DISMISS_DAYS);
        setShowBanner(false);
    };

    if (!showBanner || isStandalone()) {
        return showIOSDialog ? <IOSInstallDialog open={showIOSDialog} onOpenChange={setShowIOSDialog} /> : null;
    }

    return (
        <>
            <div
                className="fixed bottom-4 left-4 right-4 md:left-auto md:right-4 md:bottom-4 md:max-w-sm bg-scalimmo-navy text-white rounded-2xl shadow-2xl z-50 overflow-hidden border border-scalimmo-gold/30 mb-safe"
                data-testid="pwa-install-banner"
            >
                <div className="flex items-start gap-3 p-4">
                    <div className="w-11 h-11 rounded-xl bg-scalimmo-gold/15 flex items-center justify-center shrink-0">
                        <Smartphone className="w-5 h-5 text-scalimmo-gold" />
                    </div>
                    <div className="flex-1 min-w-0">
                        <p className="font-heading font-semibold text-sm">Installer Scalimmo</p>
                        <p className="text-xs text-white/70 mt-0.5 leading-snug">
                            Accédez à votre planning et messages depuis votre écran d'accueil — comme une vraie app.
                        </p>
                        <div className="flex items-center gap-2 mt-3">
                            <Button
                                size="sm"
                                onClick={handleInstall}
                                className="bg-scalimmo-gold hover:bg-scalimmo-gold/90 text-scalimmo-navy gap-1.5 h-8 px-3 text-xs font-semibold"
                                data-testid="pwa-install-btn"
                            >
                                <Download className="w-3.5 h-3.5" />
                                Installer
                            </Button>
                            <Button
                                variant="ghost"
                                size="sm"
                                onClick={handleDismiss}
                                className="text-white/60 hover:text-white hover:bg-white/10 h-8 px-2 text-xs"
                                data-testid="pwa-dismiss-btn"
                            >
                                Plus tard
                            </Button>
                        </div>
                    </div>
                    <button
                        onClick={handleDismiss}
                        className="text-white/40 hover:text-white shrink-0"
                        aria-label="Fermer"
                    >
                        <X className="w-4 h-4" />
                    </button>
                </div>
            </div>
            <IOSInstallDialog open={showIOSDialog} onOpenChange={setShowIOSDialog} />
        </>
    );
};

const IOSInstallDialog = ({ open, onOpenChange }) => {
    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-md max-h-[92vh] overflow-y-auto" data-testid="ios-install-dialog">
                <DialogHeader>
                    <DialogTitle className="font-heading text-xl flex items-center gap-2">
                        <Apple className="w-5 h-5 text-scalimmo-navy" />
                        Installer sur iPhone / iPad
                    </DialogTitle>
                    <DialogDescription>
                        Ajoutez Scalimmo à votre écran d'accueil en 3 étapes simples.
                    </DialogDescription>
                </DialogHeader>

                {/* Demo video — silent loop showing the actual install gesture */}
                <div className="relative rounded-xl overflow-hidden bg-scalimmo-navy/5 border border-scalimmo-navy/10 mt-2 flex items-center justify-center">
                    <video
                        src="/videos/install-ios.mp4"
                        poster="/videos/install-ios-poster.jpg"
                        autoPlay
                        muted
                        loop
                        playsInline
                        preload="metadata"
                        className="h-[280px] w-auto max-w-full"
                        data-testid="ios-install-video"
                        aria-label="Démonstration de l'installation"
                    />
                    <div className="absolute top-2 left-2 bg-black/55 text-white text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full backdrop-blur-sm">
                        Démo
                    </div>
                </div>

                <div className="space-y-4 mt-4">
                    <Step
                        n={1}
                        icon={<Share className="w-4 h-4" />}
                        title="Touchez le bouton Partage"
                        sub="En bas de Safari (carré avec flèche vers le haut)"
                    />
                    <Step
                        n={2}
                        icon={<Plus className="w-4 h-4" />}
                        title={`Choisissez "Sur l'écran d'accueil"`}
                        sub="Faites défiler la liste si besoin"
                    />
                    <Step
                        n={3}
                        icon={<CheckCircle2 className="w-4 h-4" />}
                        title={`Touchez "Ajouter"`}
                        sub={`L'icône Scalimmo apparaît sur votre écran d'accueil ✨`}
                    />
                </div>

                <p className="text-xs text-muted-foreground text-center pt-2">
                    Astuce : ouvrez d'abord cette page dans <strong>Safari</strong> (pas Chrome) sur iOS.
                </p>
            </DialogContent>
        </Dialog>
    );
};

const Step = ({ n, icon, title, sub }) => (
    <div className="flex items-start gap-3">
        <div className="w-8 h-8 rounded-full bg-scalimmo-navy text-white flex items-center justify-center shrink-0 text-sm font-bold">
            {n}
        </div>
        <div className="flex-1">
            <p className="font-medium text-scalimmo-navy flex items-center gap-2">
                {icon}
                {title}
            </p>
            <p className="text-xs text-muted-foreground mt-0.5">{sub}</p>
        </div>
    </div>
);

/**
 * Manual install button (e.g. used in footer / settings) for users who dismissed the banner.
 */
export const InstallAppButton = ({ className = '', ...props }) => {
    const [canInstall, setCanInstall] = useState(false);
    const [deferredPrompt, setDeferredPrompt] = useState(null);
    const [showIOS, setShowIOS] = useState(false);

    useEffect(() => {
        if (isStandalone()) return;
        const handler = (e) => {
            e.preventDefault();
            setDeferredPrompt(e);
            setCanInstall(true);
        };
        window.addEventListener('beforeinstallprompt', handler);
        if (isIOS() || isAndroid()) setCanInstall(true);
        return () => window.removeEventListener('beforeinstallprompt', handler);
    }, []);

    if (!canInstall || isStandalone()) return null;

    const click = async () => {
        if (deferredPrompt) {
            deferredPrompt.prompt();
            await deferredPrompt.userChoice;
            setDeferredPrompt(null);
        } else if (isIOS()) {
            setShowIOS(true);
        }
    };

    return (
        <>
            <Button
                onClick={click}
                variant="outline"
                className={`gap-2 ${className}`}
                data-testid="install-app-btn"
                {...props}
            >
                {isIOS() ? <Apple className="w-4 h-4" /> : <Chrome className="w-4 h-4" />}
                Installer l'app
            </Button>
            <IOSInstallDialog open={showIOS} onOpenChange={setShowIOS} />
        </>
    );
};
